import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, RotateCcw } from "lucide-react";
import { getLeaderboardMeta, newLeaderboardSeason, pauseLeaderboard, resumeLeaderboard } from "@/lib/api";

// Only one board is live today (EC2Constant.CHAMBER_OF_TRIAL). Stays a constant until a second board exists.
const BOARD = "CoT_3.0";
const errorText = (error: unknown) => error instanceof Error ? error.message : String(error);

export function TrialLeaderboardPanel({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const [confirmingSeason, setConfirmingSeason] = useState(false);
  const query = useQuery({ queryKey: ["leaderboard-meta", BOARD], queryFn: () => getLeaderboardMeta(BOARD) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["leaderboard-meta", BOARD] });
  const pause = useMutation({ mutationFn: () => pauseLeaderboard(BOARD), onSuccess: invalidate });
  const resume = useMutation({ mutationFn: () => resumeLeaderboard(BOARD), onSuccess: invalidate });
  const newSeason = useMutation({ mutationFn: () => newLeaderboardSeason(BOARD), onSuccess: () => { setConfirmingSeason(false); invalidate(); } });
  const meta = query.data;

  return <div className="tr-board">
    <section className="ws-card">
      <div className="ws-card-head"><h2>Leaderboard <span className="ws-mono ws-muted">{BOARD}</span></h2>
        {meta && (meta.submissionPaused ? <span className="pw-badge pw-badge-danger">Submissions paused</span> : <span className="pw-badge tr-badge-ok">Accepting submissions</span>)}</div>
      {query.isPending ? <div className="pw-empty tr-board-loading" role="status"><span className="pw-loading-dot" />Loading…</div>
        : meta ? <div className="tr-stats">
          <div><small>Version</small><strong>{meta.version}</strong></div>
          <div><small>Entries</small><strong>{meta.entryCount.toLocaleString()}</strong></div>
          <div className="tr-stats-action">{meta.submissionPaused
            ? <button className="pw-button pw-button-primary" onClick={() => resume.mutate()} disabled={resume.isPending || readOnly}><Play size={13} />{resume.isPending ? "Resuming…" : "Resume submissions"}</button>
            : <button className="pw-button" onClick={() => pause.mutate()} disabled={pause.isPending || readOnly}><Pause size={13} />{pause.isPending ? "Pausing…" : "Pause submissions"}</button>}</div>
        </div> : null}
      {query.isError && <div className="ws-alert" role="alert">Couldn't load status: {errorText(query.error)}</div>}
      {(pause.error || resume.error) && <div className="ws-alert tr-gap" role="alert">{errorText(pause.error ?? resume.error)}</div>}
    </section>

    <section className="ws-card tr-danger-card">
      <div className="ws-card-head"><h2>New season</h2></div>
      <div className="tr-season">
        <span className="tr-season-warn">Hides every current score from players. Can't be undone.</span>
        {!confirmingSeason
          ? <button className="pw-button pw-button-danger" onClick={() => setConfirmingSeason(true)} disabled={!meta || readOnly}><RotateCcw size={13} />Start new season…</button>
          : <span className="ws-row">
            <button className="pw-button" onClick={() => setConfirmingSeason(false)}>Cancel</button>
            <button className="pw-button pw-button-danger tr-solid-danger" onClick={() => newSeason.mutate()} disabled={!meta || newSeason.isPending || readOnly}>
              {newSeason.isPending ? "Starting…" : `Confirm: v${meta?.version ?? "?"} → v${(meta?.version ?? 0) + 1}`}</button>
          </span>}
      </div>
      {newSeason.error && <div className="ws-alert tr-gap" role="alert">{errorText(newSeason.error)}</div>}
    </section>
  </div>;
}
